import { apiFetch, ApiError, PLATFORM_RESTORE_CODES } from "./client";
import { t } from "../i18n/i18n";
function normalizeRow(r) {
    return { id: r.id, number: r.number ?? "", subject: r.subject, sentAt: r.sentAt, status: r.status, unread: r.unread, queued: r.queued };
}
// formatFileSize — байты бэка → строка «Б/КБ/МБ», как ждёт кит (help-tickets.jsx:54 выводит
// file.size текстом без дополнительного форматирования).
function formatFileSize(bytes) {
    if (bytes < 1024)
        return t("support.file_size.b", { n: bytes });
    if (bytes < 1024 * 1024)
        return t("support.file_size.kb", { n: Math.round(bytes / 1024) });
    return t("support.file_size.mb", { n: Math.round(bytes / (1024 * 1024)) });
}
function normalizeMessage(m) {
    return {
        id: m.id, from: m.from, author: m.author, date: m.date, text: m.text,
        file: m.file ? { name: m.file.name, size: formatFileSize(m.file.size), href: m.file.href } : m.file ?? null,
    };
}
function normalizeCard(r) {
    return {
        id: r.id, number: r.number ?? "", subject: r.subject, sentAt: r.sentAt, status: r.status,
        queued: r.queued, info: r.info ?? undefined, messages: r.messages.map(normalizeMessage),
    };
}
// toSupportError сводит любой отказ сети/бэка к SupportError. Ядро/Хаб не отвечают вовсе
// (Оболочка сама не смогла дозвониться до core.support) — writeCoreErr в support.go отвечает
// 503 "internal"; сетевой сбой самого fetch — тоже не ApiError. Оба случая панель кита должна
// увидеть как code:"offline" (§4.5 спеки).
function toSupportError(e) {
    if (e instanceof ApiError) {
        if (e.status === 503 && e.code === "internal")
            return { code: "offline", message: t("err.offline") };
        return { code: e.code, message: e.message };
    }
    return { code: "offline", message: t("err.offline") };
}
async function guard(run) {
    try {
        return await run();
    }
    catch (e) {
        throw toSupportError(e);
    }
}
// errorMessage — код→ключ словаря (тот же приём, что приватная errorMessage в client.ts);
// свой экземпляр, потому что postForm ниже не может переиспользовать apiFetch (multipart-тело
// вместо JSON), а client.ts — не из write-списка задачи.
function errorMessage(code) {
    const key = `err.${code}`;
    const localized = t(key);
    return localized === key ? t("err.unknown") : localized;
}
// postForm — POST multipart(FormData) с той же посадкой ошибок, что apiFetch (401 → сброс
// сессии, 503 маркер состояния платформы, иначе ApiError с кодом бэка).
async function postForm(path, form) {
    let response;
    try {
        response = await fetch(path, { method: "POST", body: form, credentials: "same-origin" });
    }
    catch {
        throw new ApiError({ status: 0, code: "offline", message: t("err.offline") });
    }
    if (response.status === 401) {
        const { useAuthStore } = await import("../state/authStore");
        useAuthStore.getState().clear();
        if (window.location.pathname !== "/login")
            window.location.assign("/login");
        throw new ApiError({ status: 401, code: "unauthenticated", message: errorMessage("unauthenticated") });
    }
    if (!response.ok) {
        let errBody = null;
        try {
            errBody = await response.json();
        }
        catch { /* тело могло не прийти */ }
        const code = errBody?.error?.code ?? `http_${response.status}`;
        if (response.status === 503 && PLATFORM_RESTORE_CODES.has(code) && errBody?.error) {
            const { usePlatformStateStore } = await import("../state/platformStateStore");
            const e = errBody.error;
            usePlatformStateStore.getState().set({
                state: e.state ?? "restoring",
                reason: e.reason ?? "",
                since: e.since ?? "",
            });
        }
        throw new ApiError({ status: response.status, code, message: errorMessage(code) });
    }
    if (response.status === 204 || response.headers.get("content-length") === "0") {
        return undefined;
    }
    return response.json();
}
function toForm(fields, file) {
    const fd = new FormData();
    for (const [k, v] of Object.entries(fields))
        fd.append(k, v);
    if (file)
        fd.append("file", file, file.name);
    return fd;
}
// makeSupportApi — поверхность, которую монтирует HelpDrawer (kit-v5 HelpApi). getWhatsNew:
// маршрута у бэка нет (нет связи с Издателем, §4.4) — честная пустая лента, панель сама покажет
// «Пока ничего нового»; мок-данных не выдумываем.
export function makeSupportApi() {
    return {
        getSupportInfo: () => guard(() => apiFetch("/api/support/info")),
        sendSupportMessage: ({ subject, text, file }) => guard(async () => {
            const res = await postForm("/api/support/send", toForm({ subject, text }, file));
            return { id: res.id, ticket: res.number, queued: res.queued };
        }),
        getWhatsNew: () => Promise.resolve({ groups: [] }),
        getTickets: () => guard(async () => {
            const res = await apiFetch("/api/support/tickets");
            return { items: res.items.map(normalizeRow) };
        }),
        getTicket: (id) => guard(async () => normalizeCard(await apiFetch(`/api/support/tickets/${encodeURIComponent(id)}`))),
        readTicket: (id) => guard(() => apiFetch(`/api/support/tickets/${encodeURIComponent(id)}/read`, { method: "POST" })),
        replyTicket: ({ id, text, file }) => guard(async () => {
            const res = await postForm(`/api/support/tickets/${encodeURIComponent(id)}/reply`, toForm({ text }, file));
            return { message: normalizeMessage(res.message) };
        }),
        reopenTicket: (id) => guard(async () => {
            const res = await apiFetch(`/api/support/tickets/${encodeURIComponent(id)}/reopen`, { method: "POST" });
            return { id: res.id, number: res.number ?? "" };
        }),
        getSupportUnread: () => guard(() => apiFetch("/api/support/unread")),
    };
}
